import {
  parsePhoneNumberFromString,
  type PhoneNumber,
} from 'libphonenumber-js';
import { z } from 'zod';

/**
 * The one phone definition for every writer of `profiles.phone_number`
 * (BAL-1073): web forms and server actions, admin edits, AI tools and the
 * audition profile backfill. The read side (`toStoredPhoneValue`) parses with
 * the same `parseInternationalPhone`.
 *
 * Rule: a number is accepted only when it carries its own country code, as a
 * leading `+` or the `00` international prefix. A national number ("078 ...")
 * is never resolved against a default country: a French "06 ..." of the right
 * length would come out as a plausible Swiss number and be saved as such.
 */

export const PHONE_E164_MESSAGE =
  'Please enter a phone number with country code (e.g., +41 78 123 45 67)';

/**
 * Parses a number that carries its own country code. Whitespace is ignored and
 * a leading `00` becomes `+`; any other formatting (dashes, dots, a "(0)" trunk
 * zero) is left to libphonenumber-js. Returns undefined for anything without a
 * country code or that does not parse as a whole.
 */
export function parseInternationalPhone(
  raw: string | null | undefined,
): PhoneNumber | undefined {
  const compact = raw ? raw.replace(/\s+/g, '') : '';

  if (!compact) {
    return undefined;
  }

  const international = compact.startsWith('00')
    ? `+${compact.slice(2)}`
    : compact;

  // No default country is passed, so libphonenumber-js itself refuses any
  // number that does not start with + at this point.
  return parsePhoneNumberFromString(international, { extract: false });
}

/**
 * The E.164 form of a number typed with its own country code, or null.
 *
 * Writes use `isPossible()` (length fits the country), which is what the
 * dancer form accepted before this module existed; the stricter `isValid()`
 * stays on the read side, where it only decides what is displayed.
 */
export function toPhoneE164(raw: string | null | undefined): string | null {
  const parsed = parseInternationalPhone(raw);

  return parsed?.isPossible() ? parsed.number : null;
}

export type PhoneWrite =
  | { ok: true; value: string | null | undefined }
  | { ok: false };

/**
 * Decides what a form that resubmits every field may write to
 * `phone_number`, given the value already stored.
 *
 * - `value: undefined` means "do not write the column": the field was absent,
 *   or the stored value came back unchanged. A legacy national number therefore
 *   never blocks saving the other fields; it waits for its owner to re-enter it.
 * - `value: null` clears the number.
 * - Any other change must convert to E.164, or the write is refused.
 */
export function resolvePhoneWrite(
  next: string | null | undefined,
  stored: string | null | undefined,
): PhoneWrite {
  if (next !== undefined && (next?.trim() ?? '') === (stored?.trim() ?? '')) {
    return { ok: true, value: undefined };
  }

  return resolveNewPhone(next);
}

/**
 * Decides what a writer that does not read the stored value may write: every
 * number it receives is new. `undefined` writes nothing, a blank value clears
 * the number, anything else must convert to E.164 or the write is refused.
 */
export function resolveNewPhone(next: string | null | undefined): PhoneWrite {
  if (next === undefined) {
    return { ok: true, value: undefined };
  }

  const trimmed = next?.trim() ?? '';

  if (!trimmed) {
    return { ok: true, value: null };
  }

  const e164 = toPhoneE164(trimmed);

  return e164 ? { ok: true, value: e164 } : { ok: false };
}

/**
 * Zod field for a phone input. An empty or blank value passes as `''`, which
 * callers read as "clear"; anything else must convert, and is output as E.164.
 */
export const phoneE164Field = z
  .string()
  .refine((value) => !value.trim() || toPhoneE164(value) !== null, {
    message: PHONE_E164_MESSAGE,
  })
  .transform((value) => (value.trim() ? (toPhoneE164(value) ?? '') : ''));
